import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/apiClient";
import { Lock } from "lucide-react";
import Logo from "@/components/Logo";
import { toast } from "sonner";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) nav("/admin", { replace: true });
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      nav("/admin");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="grid min-h-screen grid-cols-1 lg:grid-cols-2">
      <div className="relative hidden overflow-hidden bg-black p-12 text-white lg:flex lg:flex-col lg:justify-between">
        <Link to="/" className="flex items-center gap-2 font-display text-2xl font-extrabold">
          <Logo variant="dark" size={44} />
          Pristyn<span className="text-[var(--pc-green)]">.</span>
        </Link>
        <div>
          <div className="eyebrow text-[var(--pc-green)]">Admin Console</div>
          <h1 className="font-display mt-3 text-5xl font-black leading-tight tracking-tighter">
            Run the cleanest <br /> ops in Toronto.
          </h1>
          <p className="mt-5 max-w-md text-white/60">
            Manage estimates, track jobs and revenue from one quiet, focused
            dashboard.
          </p>
        </div>
        <div className="text-xs uppercase tracking-[0.3em] text-white/40">
          Authorized personnel only
        </div>
      </div>

      <div className="flex items-center justify-center bg-white p-6 md:p-12">
        <form
          data-testid="admin-login-form"
          onSubmit={submit}
          className="w-full max-w-md rounded-3xl border border-black/10 bg-white p-8 md:p-10"
        >
          <div className="grid h-12 w-12 place-items-center rounded-full bg-black text-[var(--pc-green)]">
            <Lock size={20} />
          </div>
          <h2 className="font-display mt-6 text-3xl font-black tracking-tight md:text-4xl">
            Admin Login
          </h2>
          <p className="mt-2 text-sm text-black/60">
            Sign in to manage bookings and analytics.
          </p>

          <div className="mt-6 space-y-4">
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-black/60">
                Username
              </label>
              <input
                data-testid="login-email"
                type="text"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="username"
                autoComplete="username"
                className="w-full rounded-xl border border-black/15 bg-white px-4 py-3 text-sm outline-none focus:border-[var(--pc-green)] focus:ring-4 focus:ring-[rgba(0,230,118,0.18)]"
              />
            </div>
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-black/60">
                Password
              </label>
              <input
                data-testid="login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full rounded-xl border border-black/15 bg-white px-4 py-3 text-sm outline-none focus:border-[var(--pc-green)] focus:ring-4 focus:ring-[rgba(0,230,118,0.18)]"
              />
            </div>
          </div>

          <button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="pc-btn-primary mt-8 w-full justify-center disabled:opacity-60"
          >
            {loading ? "Signing in…" : "Sign In"}
          </button>

          <Link
            to="/"
            data-testid="login-back-home"
            className="mt-6 block text-center text-sm text-black/50 hover:text-black"
          >
            ← Back to site
          </Link>
        </form>
      </div>
    </div>
  );
}
